import {Fragment, PropsWithChildren, useContext, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {CareJourneyDetailsResponse} from "@/api/dto/care-journey";
import {StandardSemiBoldTypography} from "patient-ping-remedy/packages/typography";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {colors} from "patient-ping-remedy/packages/theme";
import SlideInPane from 'patient-ping-remedy/packages/slide-in-pane';
import FormPDFViewer from '@clients/profile_page/forms_tab/FormPDFViewer';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {useMixpanel} from "@/hooks/useMixpanel";
import {MixpanelEventType, MixpanelTaskEvent} from "@/api/dto/mixpanel";
import FileHelpers from '@/helpers/file_helpers';
import Helpers from "@/helpers/helpers";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import {AxiosError} from "axios";
import Loading from "@common/Loading";
import {
  SectionHeaderRow,
  SectionTitleRow,
  StatusBadge,
  StatusText
} from "@/components/pages/tasks/task_edit/progress/CareNoteSection.styles";
import {HeaderButtonWrapper} from "@common/FilterDrawer.styles";

type Props = PropsWithChildren & {
  careJourneyDetails: CareJourneyDetailsResponse;
}

const CareNotePreview = (props: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();

  const [pdfViewerState, setPdfViewerState] = useState<{ isOpen: boolean; url: string; fileName: string}>({
    isOpen: false,
    url: '',
    fileName: ''
  });

  const {mutate: handlePreview, isPending: previewPending} = useMutation({
    mutationFn: async () => {
      return await carecoApi!.getCareNotePreview(
        props.careJourneyDetails.careJourneyInstanceId,
                currentRoster!.id
      );
    },
    onSuccess: (response) => {
      if (response) {
        const arrayBuffer = FileHelpers.base64ToArrayBuffer(response.document.PDF_DOC);
        const blob = new Blob([arrayBuffer], {type: 'application/pdf'});
        const url = URL.createObjectURL(blob);
        setPdfViewerState(prevState => {
          if (prevState.isOpen) {
            return {...prevState, url: url + '#toolbar=0', fileName: response.resolvedTitle};
          } else {
            URL.revokeObjectURL(url);
            return prevState;
          }
        });
      }
    },
    onError: (error: AxiosError) => {
      setPdfViewerState({isOpen: false, url: '', fileName: ''});
      Helpers.alertOnError(`Failed to generate care note. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const closePdfViewer = () => {
    if (pdfViewerState.url) {
      URL.revokeObjectURL(pdfViewerState.url.replace('#toolbar=0', ''));
    }
    setPdfViewerState({isOpen: false, url: '', fileName: ''});
  };

  const intakeCompleted = !!props.careJourneyDetails.steps
    .find(step => step.stepName === 'intake' && step.status === 'completed');

  const recipients = new Set(
    props.careJourneyDetails.orchestrationEngineDetails?.directMessageDeliveries
      ?.map(delivery => delivery.directAddress) ?? []).size;

  const handleDownloadPreview = (url: string, fileName: string) => {
    FileHelpers.downloadFile(url, `${fileName}.pdf`);

    if (mixpanelEventProperties && currentRoster) {
      const event: MixpanelTaskEvent = {
        eventName: MixpanelEventType.BIH_CARE_NOTE_DOWNLOADED,
        eventType: props.careJourneyDetails.eventType,
        careJourneyName: props.careJourneyDetails.journeyType,
        ...mixpanelEventProperties,
        ...getCurrentGroupDetails(currentRoster)
      };
      carecoApi?.postMixpanelEvent(event);
    }
  };

  return <Fragment>
    <SectionHeaderRow>
      <SectionTitleRow>
        <StandardSemiBoldTypography>Care note</StandardSemiBoldTypography>
        {recipients > 0 &&
        <StatusBadge bgColor={colors.gray1}>
          <StatusText textColor={colors.charcoal}>
            {recipients} {recipients === 1 ? 'recipient' : 'recipients'}
          </StatusText>
        </StatusBadge>
        }
      </SectionTitleRow>
      <SectionTitleRow>
        <Button
          onClick={() => {
            if (mixpanelEventProperties && currentRoster) {
              const event: MixpanelTaskEvent = {
                eventName: MixpanelEventType.BIH_PREVIEW_CARE_NOTE_SELECTED,
                eventType: props.careJourneyDetails.eventType,
                careJourneyName: props.careJourneyDetails.journeyType,
                ...mixpanelEventProperties,
                ...getCurrentGroupDetails(currentRoster)
              };
              carecoApi?.postMixpanelEvent(event);
            }
            handlePreview();
            setPdfViewerState(prevState => ({isOpen: true, url: prevState.url, fileName: prevState.fileName}));
          }}
          styleType={StyleType.TERTIARY}
          disabled={!intakeCompleted || previewPending}
        >
         Preview Care note
        </Button>
        {props.children}
      </SectionTitleRow>
    </SectionHeaderRow>

    <SlideInPane
      onClose={closePdfViewer}
      isOpen={pdfViewerState.isOpen}
      showNav={false}
      headerText="Care note preview"
      width="150rem"
      headerChildren={
        pdfViewerState.url && (
          <HeaderButtonWrapper>
            <Button
              onClick={() => handleDownloadPreview(pdfViewerState.url, pdfViewerState.fileName)}
              styleType={StyleType.SECONDARY}>
              Download
            </Button>
          </HeaderButtonWrapper>
        )
      }
    >
      {!pdfViewerState.url && <Loading/>}
      {pdfViewerState.url && (
        <FormPDFViewer id="care-note-preview-pdf-viewer" url={pdfViewerState.url}/>
      )}
    </SlideInPane>
  </Fragment>;
};

export default CareNotePreview;
